import { useState, useEffect } from "react";
import { useLoaderData, useParams, useOutletContext } from "react-router-dom";

import CreditPolicy from "./CreditPolicy";
import { arrangeDirectors } from "../Directors";
import { UnderwritingResponse } from "./interface";
import { userActiveStruct } from "../Directors/helper";
import { DropdownValue } from "../CoApplicantTab/AddCoApplicant/interface";
import { FinBoxResponse } from "../../shared/services/APIResponse";
import { LoanApplicationService } from "../../services/LoanApplication";
import { CustomLoaderFunctionArgs } from "../../shared/router/RouteLoader";
import DropdownWithSearch from "../../components/atomic/DropdownWithSearch";
import { IMultiSelectDropdownOption } from "../../components/atomic/Dropdown";
import { DropdownWithSearchOption } from "../../components/atomic/DropdownWithSearch/interface";
import GetAuthContext from "../../../../src/shared/context/AuthContext";
import { ORG_IDS } from "../../shared/utils/constants";

export async function loader({
  pageName,
  params,
  getToken,
  isLenderDashboard = true
}: CustomLoaderFunctionArgs) {
  const { userID, sourceEntityID, loanID: loanApplicationID } = params;

  return await LoanApplicationService.getUnderwriting({
    token: getToken(),
    pageName: pageName!,
    isLenderDashboard,
    sourceEntityID: !!userID ? sourceEntityID : undefined,
    userID: !!userID ? userID : undefined,
    ...(!userID ? { loanApplicationID: loanApplicationID! } : {})
  });
}

export default function CreditPolicies({ pageName }: { pageName: string }) {
  const { selectedApplicant } = useOutletContext() as any;
  const { getPageConfig, userState, fetchToken } = GetAuthContext();
  const pageConfig = getPageConfig();
  const params = useParams();
  const { loanID, userID, sourceEntityID } = params;

  const { loanDetails, leadsDetails } = useOutletContext() as any;

  const { data: multiUserCreditPolicies } = useLoaderData() as FinBoxResponse<
    UnderwritingResponse[]
  >;

  const isLeadContext = !!userID && !loanID;

  const details = isLeadContext ? leadsDetails : loanDetails;

  const isMFL_LAP = pageConfig[pageName!]?.isMFLLAP ?? false;
  const isABFL_CO_LENDING = userState?.organizationID === ORG_IDS.ABFL;
  const isMFL = userState?.organizationID === ORG_IDS.MFL;

  const isDirectorOperation = isABFL_CO_LENDING
    ? false
    : ["Private Limited", "Partnership"].includes(details?.constitution);

  const [selected, setSelected] = useState<DropdownValue>({} as DropdownValue);

  const optionValue = isDirectorOperation
    ? [
        { name: details?.name, userID: details?.userID },
        ...arrangeDirectors(details?.directorDetails ?? [])
      ]
    : multiUserCreditPolicies;

  const dropdownOptions =
    (optionValue || [])?.length > 0
      ? (optionValue || []).map((policies: any, idx: number) => {
          let userType: any = "";
          let userValue;
          const isPrimaryApplicant =
            !isDirectorOperation && policies.userType === "primary_applicant";
          const isDirectorMatch =
            isDirectorOperation && details?.userID === policies?.userID;

          if (isDirectorMatch) {
            userType = userActiveStruct({
              name: details?.name,
              active: policies?.active
            });

            userValue = details?.userID;
          } else {
            userType = isDirectorOperation
              ? userActiveStruct({
                  name: policies?.name,
                  active: policies?.active
                })
              : isPrimaryApplicant
                ? "Primary Applicant"
                : `Co-Applicant ${idx}`;

            userValue = policies?.userID;
          }

          return {
            name: userType,
            value: userValue ?? ""
          } as DropdownWithSearchOption & IMultiSelectDropdownOption;
        })
      : [];

  // Update selected when dropdownOptions change (initial load)
  useEffect(() => {
    dropdownOptions?.length > 0 &&
      !selected?.value &&
      setSelected(dropdownOptions[0] as DropdownValue);
  }, [dropdownOptions]);

  if (!multiUserCreditPolicies?.length) {
    return <div className="p-4">Credit Policies not found.</div>;
  }

  const placeHolderDir = (isPlaceholder = false) => {
    const suffix = isPlaceholder ? "s" : "";
    if (isDirectorOperation)
      return `${
        details?.constitution === "Partnership" ? "Partner" : "Director"
      }${suffix}`;

    return !isMFL_LAP ? `Co-Applicant${suffix}` : "Applicant Type";
  };

  return (
    <div>
      {!isMFL_LAP && !isMFL && dropdownOptions?.length > 1 && (
        <div className="pt-8 w-fit">
          <DropdownWithSearch
            hasSearch={false}
            label={placeHolderDir()}
            onChange={setSelected}
            selectedItems={selected}
            options={dropdownOptions}
            placeholder={`Select ${placeHolderDir(true)}`}
          />
        </div>
      )}

      <CreditPolicy
        userID={!isMFL_LAP ? selected?.value : selectedApplicant?.userID}
        pageName={pageName}
        creditPolicies={
          multiUserCreditPolicies?.find(
            (policies) => policies.userID === selected.value
          )?.underwritingData ?? []
        }
      />
    </div>
  );
}
